import MainTable from '@Components/MainTable';
import { MainTableRow } from '@Components/MainTable/MainTable';
import { onPageChangeRequestMeta } from '@Components/MainTable/MainTable.types';
import RoundButton from '@Components/UI/Buttons/RoundButton';
import EdIcon from '@Components/UI/Utilities/EdIcon/EdIcon';
import EdConfirmModal from '@Components/UI/Modals/EdConfirmModal/EdConfirmModal';
import { TableHeader } from '@Features/generics/generics.type';
import { useTypedSelector } from '@Features/store';
import { useSnackbar } from '@Providers/useSnackbar';
import { FlexLayout } from '@Styled/utilities';
import { Chip, TableCell, Tooltip } from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import {
	attendanceReportExportData,
	attendanceReportGetAll,
} from '../../Slices/AttendanceReport.slice';
import { AttendanceReportRequester } from '../../Services/AttendanceReport.requester';
import {
	AttendanceCheckinRow,
	AttendanceReportMeta,
} from '../../Types/AttendanceReport.types';
import NotificationRecipientCell from '../Tests/Components/NotificationRecipientCell';
import AttendanceCards from './Components/AttendanceCards';
import SendAttendanceDialog from './Components/SendAttendanceDialog';

// Pixel widths so the table takes the room it needs and scrolls
// horizontally; the first three (identity) columns stay frozen, and
// their widths are the sticky left offsets in TableScroller below.
const TABLE_HEADS: Array<TableHeader> = [
	{ displayName: 'Code', width: '90px' },
	{ displayName: 'Username', width: '120px' },
	{ displayName: 'Name', width: '170px' },
	{ displayName: 'Phone', width: '110px' },
	{ displayName: 'Parent', width: '110px' },
	{ displayName: 'Group', width: '150px' },
	{ displayName: 'Attendance', width: '110px' },
	{ displayName: 'Arrival', width: '90px' },
	{ displayName: 'HW', width: '90px' },
	{ displayName: 'Score', width: '90px' },
	{ displayName: 'WhatsApp', width: '240px' },
];
const TABLE_MIN_WIDTH = 1370;

const HOMEWORK_LABELS: Record<string, string> = {
	done: 'Done',
	partial: 'Partial',
	not_done: 'Not done',
};

/**
 * Reports → Attendance: the lecture's FULL roster (present / late / no-show
 * / missing), searchable by name / username / phone / parent phone / code,
 * filterable per group, with per-parent WhatsApp delivery, one
 * "Send to unsent" action and inline per-row Resend.
 */
const AttendanceTable = () => {
	const dispatch = useDispatch();
	const { displaySnackbar } = useSnackbar();
	const { currentCourse } = useTypedSelector((state) => state.classroom);
	const {
		fetchAll,
		filters,
		meta: rawMeta,
		selectedFilters,
		query,
		sortBy,
	} = useTypedSelector((state) => state.AttendanceReport);
	const meta = rawMeta as unknown as AttendanceReportMeta | null;
	const rows = fetchAll as unknown as AttendanceCheckinRow[];

	const [refetch, setRefetch] = useState(false);
	const [sendOpen, setSendOpen] = useState(false);
	const [sending, setSending] = useState(false);
	const [resending, setResending] = useState(false);
	const [resendTarget, setResendTarget] = useState<{
		checkinId: number;
		recipient: 'student' | 'parent';
	} | null>(null);

	const courseId = currentCourse?.id;

	const onPageChangeRequest = useCallback(
		({
			activePage,
			filters,
			query,
			sortBy,
			perPage,
		}: onPageChangeRequestMeta) => {
			if (!courseId) return;
			dispatch(
				attendanceReportGetAll({
					page: activePage,
					filters,
					perPage: perPage ?? 50,
					query,
					sortBy,
					id: courseId,
				})
			);
			setRefetch(false);
		},
		[courseId]
	);

	// The active group filter, for the send payload and the dialog label.
	const groupFilter = useMemo(() => {
		let value: number | undefined;
		(selectedFilters ?? []).forEach((filter: any) => {
			const raw = filter?.name === 'group_id' ? filter.value : filter?.group_id;
			if (raw !== undefined && raw !== null && raw !== '') value = Number(raw);
		});
		if (value === undefined) return { id: undefined, label: null as string | null };
		const options =
			(filters ?? []).find((entry: any) => entry.value === 'group_id')
				?.options ?? [];
		const option = options.find(
			(candidate: any) => Number(candidate.value) === value
		);
		return { id: value, label: option?.name ?? String(value) };
	}, [selectedFilters, filters]);

	const refresh = () => setRefetch(true);

	const location = useLocation();
	const history = useHistory();
	const cardFilters = useMemo(() => {
		const params = new URLSearchParams(location.search);
		return {
			status: params.get('FILTER_status'),
			wa: params.get('FILTER_wa'),
		};
	}, [location.search]);
	const applyCardFilter = (name: 'status' | 'wa' | null, value?: string) => {
		const params = new URLSearchParams(location.search);
		if (!name) {
			params.delete('FILTER_status');
			params.delete('FILTER_wa');
		} else if (params.get(`FILTER_${name}`) === value) {
			// Clicking the active card clears its filter.
			params.delete(`FILTER_${name}`);
		} else {
			params.set(`FILTER_${name}`, value!);
		}
		params.set('page', '1');
		history.push({ search: params.toString() });
	};

	const performSend = async () => {
		if (!courseId) return;
		setSending(true);
		try {
			const {
				data: { data },
			} = await AttendanceReportRequester.getInstance().send({
				course_id: courseId,
				...(groupFilter.id !== undefined ? { group_id: groupFilter.id } : {}),
			});
			displaySnackbar('success', `Queued ${data.queued} WhatsApp message(s)`);
			setSendOpen(false);
			refresh();
		} catch (error: any) {
			displaySnackbar(
				'error',
				error?.response?.data?.message ?? 'Unable to queue the messages'
			);
		} finally {
			setSending(false);
		}
	};

	const performResend = async (
		checkinId: number,
		recipient: 'student' | 'parent'
	) => {
		if (!courseId) return;
		setResending(true);
		try {
			await AttendanceReportRequester.getInstance().resendNotification({
				course_id: courseId,
				checkin_id: checkinId,
				recipient,
			});
			displaySnackbar('success', `WhatsApp message to the ${recipient} re-queued`);
			refresh();
		} catch (error: any) {
			displaySnackbar(
				'error',
				error?.response?.data?.message ?? 'Unable to resend the message'
			);
		} finally {
			setResending(false);
		}
	};

	const requestResend = (
		checkinId: number,
		recipient: 'student' | 'parent',
		status?: string | null
	) => {
		if (status === 'delivered' || status === 'read') {
			setResendTarget({ checkinId, recipient });
		} else {
			performResend(checkinId, recipient);
		}
	};

	const notifications = meta?.notifications;
	const sendDisabledReason = !notifications?.whatsapp_enabled
		? 'WhatsApp is not enabled for this platform — see Settings → WhatsApp'
		: !notifications?.attendance_template_ready
		? 'The attendance template is not configured — Settings → WhatsApp (admin)'
		: (meta?.delivery?.unsent ?? 0) === 0
		? 'Every check-in of this scope already has a message'
		: '';

	const attendanceChip = (row: AttendanceCheckinRow) => {
		if (row.attendance === 'missing')
			return <Chip size="small" label="Missing" sx={{ backgroundColor: '#ffab00', color: '#fff' }} />;
		if (row.attendance === 'no_show')
			return <Chip size="small" label="No-show" sx={{ backgroundColor: '#ff3100', color: '#fff' }} />;
		const label =
			row.attendance === 'present_elsewhere'
				? 'Elsewhere'
				: row.late
				? 'Late'
				: 'Present';
		const color =
			row.attendance === 'present_elsewhere'
				? '#a4a4a4'
				: row.late
				? '#ffab00'
				: '#00d66b';
		const chip = (
			<Chip size="small" label={label} sx={{ backgroundColor: color, color: '#fff' }} />
		);
		return row.after_close ? (
			<Tooltip title="Scanned after the run was closed">{chip}</Tooltip>
		) : (
			chip
		);
	};

	const scoreCell = (row: AttendanceCheckinRow) => {
		if (!meta?.exam) return 'No exam';
		if (row.attendance === 'no_show' || row.attendance === 'missing') return '-';
		const total = meta.exam.overall_score;
		return row.score === null ? `-/${total}` : `${row.score}/${total}`;
	};

	const homeworkCell = (row: AttendanceCheckinRow) => {
		if (!meta?.homework_check) return '—';
		if (row.attendance === 'no_show' || row.attendance === 'missing') return '-';
		// Null survived the roster's enrolled-present default: no verdict was
		// recorded (visitor / present elsewhere).
		if (!row.homework_status) return '-';
		return HOMEWORK_LABELS[row.homework_status];
	};

	return (
		<>
			<EdConfirmModal
				open={!!resendTarget}
				text={`This WhatsApp message was already delivered. Send it again to the ${
					resendTarget?.recipient ?? ''
				}?`}
				handleClose={(close, confirm) => {
					const target = resendTarget;
					setResendTarget(null);
					if (confirm && target) performResend(target.checkinId, target.recipient);
				}}
			/>
			<SendAttendanceDialog
				open={sendOpen}
				meta={meta}
				groupLabel={groupFilter.label}
				sending={sending}
				onClose={() => setSendOpen(false)}
				onConfirm={performSend}
			/>
			<AttendanceCards
				counts={meta?.counts}
				delivery={meta?.delivery}
				active={cardFilters}
				onFilter={applyCardFilter}
			/>
			<FlexLayout justifyContent="flex-end" gridGap="0.75rem">
				<Tooltip title={sendDisabledReason}>
					<span>
						<RoundButton
							variant="contained"
							disabled={!!sendDisabledReason}
							onClick={() => setSendOpen(true)}
							startIcon={<EdIcon>send</EdIcon>}
						>
							Send to unsent ({meta?.delivery?.unsent ?? 0})
						</RoundButton>
					</span>
				</Tooltip>
				<RoundButton
					onClick={() =>
						courseId &&
						dispatch(
							attendanceReportExportData({
								page: 1,
								perPage: meta?.per_page ?? 50,
								filters: selectedFilters,
								query: query ?? '',
								sortBy,
								id: courseId,
							})
						)
					}
					startIcon={<EdIcon>south</EdIcon>}
					variant="contained"
				>
					Export
				</RoundButton>
			</FlexLayout>
			<TableScroller>
			<MainTable
				tableMinWidth={TABLE_MIN_WIDTH}
				total={meta?.total ?? 0}
				refetch={refetch}
				pageNum={meta?.current_page}
				tableFilters={filters}
				tableHeads={TABLE_HEADS}
				totalPages={meta?.last_page ?? 0}
				RowsperPage={Number(meta?.per_page ?? 50)}
				onPageChangeRequest={onPageChangeRequest}
				renderItems={() =>
					rows.map((row) => (
						<MainTableRow key={`${row.student_id}`}>
							<CellStyled>{row.code ?? '-'}</CellStyled>
							<CellStyled>{row.username ?? '-'}</CellStyled>
							<CellStyled>
								{`${row.first_name ?? ''} ${row.last_name ?? ''}`.trim() || '-'}
							</CellStyled>
							<CellStyled>{row.phone_number ?? '-'}</CellStyled>
							<CellStyled>{row.parent_phone ?? '-'}</CellStyled>
							<CellStyled>
								{row.assigned_group_name ?? '-'}
								{row.checkin_group_name &&
									row.checkin_group_name !== row.assigned_group_name && (
										<GroupNote>with {row.checkin_group_name}</GroupNote>
									)}
							</CellStyled>
							<CellStyled>{attendanceChip(row)}</CellStyled>
							<CellStyled>
								{row.attendance === 'present_here' ||
								row.attendance === 'present_elsewhere'
									? row.arrived_at
										? new Date(row.arrived_at).toLocaleTimeString([], {
												hour: '2-digit',
												minute: '2-digit',
										  })
										: '-'
									: '-'}
							</CellStyled>
							<CellStyled>{homeworkCell(row)}</CellStyled>
							<CellStyled>
								<ScoreValue $result={row.result}>{scoreCell(row)}</ScoreValue>
							</CellStyled>
							<CellStyled>
								<FlexLayout flexDirection="column" gridGap="0.25rem">
									{row.checkin_id !== null ? (
										<NotificationRecipientCell
											who="Parent"
											channel="whatsapp"
											status={row.whatsapp_parent_status}
											errorCode={row.whatsapp_parent_error_code}
											error={row.whatsapp_parent_error}
											updatedAt={row.whatsapp_parent_updated_at}
											resending={resending}
											onResend={() =>
												requestResend(
													row.checkin_id!,
													'parent',
													row.whatsapp_parent_status
												)
											}
										/>
									) : (
										<Muted>not messaged</Muted>
									)}
									{row.checkin_id !== null && row.whatsapp_student_status && (
										<NotificationRecipientCell
											who="Student"
											channel="whatsapp"
											status={row.whatsapp_student_status}
											errorCode={row.whatsapp_student_error_code}
											error={row.whatsapp_student_error}
											updatedAt={row.whatsapp_student_updated_at}
											resending={resending}
											onResend={() =>
												requestResend(
													row.checkin_id!,
													'student',
													row.whatsapp_student_status
												)
											}
										/>
									)}
								</FlexLayout>
							</CellStyled>
						</MainTableRow>
					))
				}
			/>
			</TableScroller>
		</>
	);
};

const CellStyled = styled(TableCell)`
	font-size: 0.875rem;
`;

// Freeze Code / Username / Name during horizontal scroll. The left
// offsets are the first two columns' widths from TABLE_HEADS; the
// solid background hides the columns sliding underneath (rows are
// plain white, no stripe or hover color to match).
const TableScroller = styled.div`
	table {
		border-collapse: separate;
		border-spacing: 0;
	}
	th:nth-child(-n + 3),
	td:nth-child(-n + 3) {
		position: sticky;
		background: #fff;
		z-index: 2;
	}
	thead th:nth-child(-n + 3) {
		z-index: 3;
	}
	th:nth-child(1),
	td:nth-child(1) {
		left: 0;
	}
	th:nth-child(2),
	td:nth-child(2) {
		left: 90px;
	}
	th:nth-child(3),
	td:nth-child(3) {
		left: 210px;
		box-shadow: 6px 0 6px -5px rgba(0, 0, 0, 0.15);
	}
`;

const GroupNote = styled.div`
	font-size: 0.7rem;
	color: #ffab00;
`;

const Muted = styled.span`
	font-size: 0.75rem;
	color: #a4a4a4;
`;

const ScoreValue = styled.span<{ $result: 'passed' | 'failed' | null }>`
	color: ${({ $result }) =>
		$result === 'passed' ? '#00d66b' : $result === 'failed' ? '#ff3100' : 'inherit'};
	font-weight: ${({ $result }) => ($result ? 600 : 400)};
`;

export default AttendanceTable;
